import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "../../services/api";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useChat } from "../../contexts/ChatContext";
import { getAppLanguage, toLocale } from "../../utils/appLanguage";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

type EnergyMatchResponse = {
  compatibility_summary?: {
    overall_score?: number;
    status_label?: string;
    battery_config?: {
      level?: number;
      style?: string;
      color_theme?: string;
      hex_code?: string;
      is_unstable?: boolean;
    };
  };
  user_profile?: {
    full_name?: string;
    date_of_birth?: string;
    time_of_birth?: string | null;
    place_of_birth?: string | null;
  };
  user_birth_details?: {
    full_name?: string;
    date_of_birth?: string;
    time_of_birth?: string | null;
    place_of_birth?: string | null;
  };
  partner_profile?: {
    full_name?: string;
    date_of_birth?: string;
    time_of_birth?: string | null;
    place_of_birth?: string | null;
  };
  partner_birth_details?: {
    full_name?: string;
    date_of_birth?: string;
    time_of_birth?: string | null;
    place_of_birth?: string | null;
  };
  analysis_modules?: Array<{
    key?: "love" | "communication" | "lifestyle" | string;
    title?: string;
    score?: number;
    description?: string;
  }>;
  astro_details?: {
    method?: string;
    clashing_points?: string[];
    supporting_points?: string[];
    visual_data?: {
      primary_planet?: string;
      secondary_planet?: string;
    };
  };
  ai_suggestion?: {
    text?: string;
    button_label?: string;
  };
  life_graph?: {
    categories: string[];
    values: number[];
  };
  detailed_insights?: {
    energy_flow?: string;
    emotional_connection?: string;
    action_timing?: string;
    communication_tips?: string;
  };
  theme?: string;
  pages?: Array<{
    pageId: string;
    title?: string;
    components?: {
      scoreGauge?: {
        value: number;
        label: string;
      };
      lifeGraph?: {
        type: string;
        categories: string[];
        value: number[];
      };
      summary?: Array<{
        type: string;
        title: string;
        text: string;
      }>;
    };
    cards?: Array<{
      id: string;
      title: string;
      icon: string;
      description: string;
    }>;
    chatHistory?: Array<{
      sender: string;
      text: string;
    }>;
    quickReplies?: string[];
  }>;
  summary?: Array<{
    type: "positive" | "adjustment";
    title: string;
    text: string;
  }>;
};

// Type for the API response structure
type ApiResponse = {
  success: boolean;
  message: string;
  data: any;
  promptSource: any;
  historyId?: string;
  aiResponse: string;
  subCategoryUsed: string | null;
};

// Builds the compatibility request sent to the chat. It is written in the
// user's preferred language, because the reply is written in the language of
// the message.
function buildCountryCompatibilityMessage(
  _country: string,
  p1Name: string,
  p1Dob: string,
  p1Time: string,
  p1Place: string,
  p2Name: string,
  p2Dob: string,
  p2Time: string,
  p2Place: string,
  relation: string,
): string {
  const t = (key: string) => i18n.t(`energy.prompt.${key}`);
  const p1 = `${t("name")}: ${p1Name}, ${t("dob")}: ${p1Dob}${p1Time ? `, ${t("time")}: ${p1Time}` : ""}${p1Place ? `, ${t("place")}: ${p1Place}` : ""}`;
  const relationLabel = i18n.t(`energy.rel.${relation}`, { defaultValue: relation });
  const p2 = `${t("name")}: ${p2Name}, ${t("dob")}: ${p2Dob}${p2Time ? `, ${t("time")}: ${p2Time}` : ""}${p2Place ? `, ${t("place")}: ${p2Place}` : ""}, ${t("relation")}: ${relationLabel}`;
  return `${t("head")}\n${t("person1")}: ${p1}\n${t("person2")}: ${p2}`;
}

const BOT_SENDER = "bot";

const EnergyMatchDesign: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation(); // add
  const isEnergyMatchV3 = location.pathname === "/energyMatchV3";
  const botName = isEnergyMatchV3 ? "Cosmic" : "Astria";
  // Form state
  const [person1Name, setPerson1Name] = useState("");
  const [person1Date, setPerson1Date] = useState("");
  const [person1Time, setPerson1Time] = useState("");
  const [person1Place, setPerson1Place] = useState("");
  const [person2Name, setPerson2Name] = useState("");
  const [person2Date, setPerson2Date] = useState("");
  const [person2Time, setPerson2Time] = useState("");
  const [person2Place, setPerson2Place] = useState("");
  const [historyId, setHistoryId] = useState("");
  const [person2Relation, setPerson2Relation] = useState("Partner");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startNewChat, sendMessageStream, setCurrentChat } = useChat();

  // Country module mode — when opened from an Astria subcategory
  const countryParam = searchParams.get("country");
  const categoryIdParam = searchParams.get("categoryId");
  const subCategoryIdParam = searchParams.get("subCategoryId");
  const isCountryMode = Boolean(
    countryParam && categoryIdParam && subCategoryIdParam,
  );

  const pageTransition = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      },
    },
  };

  // UI state
  const [currentPage, setCurrentPage] = useState<
    "form" | "prediction" | "insights" | "chat"
  >("form");
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [resultData, setResultData] = useState<EnergyMatchResponse | null>(
    null,
  );
  const [selectedInsight, setSelectedInsight] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<
    Array<{ sender: string; text: string }>
  >([{ sender: BOT_SENDER, text: i18n.t("energy.greeting") }]);
  const [chatInput, setChatInput] = useState("");
  const [quickReplies, setQuickReplies] = useState<string[]>([
    i18n.t("energy.quick.advice"),
    i18n.t("energy.quick.summary"),
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Formatting helpers with auto-formatting
  const formatDateValue = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    const limitedDigits = digits.slice(0, 8);
    let formatted = "";
    for (let i = 0; i < limitedDigits.length; i++) {
      if (i === 2 || i === 4) {
        formatted += "/";
      }
      formatted += limitedDigits[i];
    }
    return formatted;
  };

  const formatTimeValue = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    const limitedDigits = digits.slice(0, 4);
    let formatted = "";
    for (let i = 0; i < limitedDigits.length; i++) {
      if (i === 2) {
        formatted += ":";
      }
      formatted += limitedDigits[i];
    }
    return formatted;
  };

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatDateValue(rawValue);
    setter(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length) {
          newCursorPos = 4;
        } else if (
          cursorPosition === 6 &&
          newFormatted.length > oldFormatted.length
        ) {
          newCursorPos = 7;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatTimeValue(rawValue);
    setter(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length) {
          newCursorPos = 4;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const validateDate = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    if (!value) return;
    const parts = value.split("/");
    if (parts.length === 3) {
      let day = parseInt(parts[0], 10);
      let month = parseInt(parts[1], 10);
      let year = parseInt(parts[2], 10);
      if (isNaN(day)) day = 1;
      if (day < 1) day = 1;
      if (day > 31) day = 31;
      if (isNaN(month)) month = 1;
      if (month < 1) month = 1;
      if (month > 12) month = 12;
      if (isNaN(year)) year = new Date().getFullYear();
      if (year < 1900) year = 1900;
      if (year > 2099) year = 2099;
      const formatted = `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
      setter(formatted);
    }
  };

  const validateTime = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    if (!value) return;
    const parts = value.split(":");
    if (parts.length === 2) {
      let hour = parseInt(parts[0], 10);
      let minute = parseInt(parts[1], 10);
      if (isNaN(hour)) hour = 0;
      if (hour < 0) hour = 0;
      if (hour > 23) hour = 23;
      if (isNaN(minute)) minute = 0;
      if (minute < 0) minute = 0;
      if (minute > 59) minute = 59;
      const formatted = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      setter(formatted);
    }
  };

  const getCategoryId = () => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    return params.get("category") || params.get("CategoryId");
  };

  // Parse AI response from the API structure
  const parseAiResponse = (rawResponse: string): EnergyMatchResponse => {
    try {
      // Clean the response
      const cleaned = rawResponse
        .replace(/```json/gi, "")
        .replace(/```/g, "")
        .trim();
      const jsonSafe = cleaned.replace(/,\s*([}\]])/g, "$1");
      const parsed = JSON.parse(jsonSafe);

      // Extract data from the pages structure
      let overallScore = 78;
      let lifeGraphCategories = [
        t("energy.metric.emotionalFlow"),
        t("energy.metric.mentalRhythm"),
        t("energy.metric.actionDrive"),
        t("energy.metric.harmonyField"),
        t("energy.metric.commEnergy"),
      ];
      let lifeGraphValues = [65, 72, 58, 80, 70];
      let summaryItems = [
        {
          type: "positive" as const,
          title: t("energy.summary.good.title"),
          text: t("energy.summary.good.text"),
        },
        {
          type: "adjustment" as const,
          title: t("energy.summary.tune.title"),
          text: t("energy.summary.tune.text"),
        },
      ];
      let insightCards = [
        {
          id: "energy_flow",
          title: t("energy.insight.energy_flow.title"),
          icon: "🌊",
          description: t("energy.insight.energy_flow.desc"),
        },
        {
          id: "emotional",
          title: t("energy.insight.emotional.title"),
          icon: "💖",
          description: t("energy.insight.emotional.desc"),
        },
        {
          id: "action",
          title: t("energy.insight.action.title"),
          icon: "⏰",
          description: t("energy.insight.action.desc"),
        },
        {
          id: "communication",
          title: t("energy.insight.communication.title"),
          icon: "💬",
          description: t("energy.insight.communication.desc"),
        },
      ];
      // Find prediction page
      const predictionPage = parsed.pages?.find(
        (p: any) => p.pageId === "P2_Prediction",
      );
      if (predictionPage) {
        if (predictionPage.components?.scoreGauge?.value) {
          overallScore = predictionPage.components.scoreGauge.value;
        }
        if (predictionPage.components?.lifeGraph) {
          lifeGraphCategories = predictionPage.components.lifeGraph.categories;
          lifeGraphValues = predictionPage.components.lifeGraph.value;
        }
        if (predictionPage.components?.summary) {
          summaryItems = predictionPage.components.summary;
        }
      }

      // Find insights page
      const insightsPage = parsed.pages?.find(
        (p: any) => p.pageId === "P3_Insights",
      );
      if (insightsPage && insightsPage.cards) {
        insightCards = insightsPage.cards.map((card: any) => ({
          id: card.id,
          title: card.title,
          icon:
            card.icon === "wave"
              ? "🌊"
              : card.icon === "heart"
                ? "💖"
                : card.icon === "clock"
                  ? "⏰"
                  : "💬",
          description: card.description,
        }));
      }

      return {
        compatibility_summary: {
          overall_score: overallScore,
          status_label: t("energy.statusLabel"),
          battery_config: {
            level: overallScore,
            hex_code: "#C084FC",
          },
        },
        life_graph: {
          categories: lifeGraphCategories,
          values: lifeGraphValues,
        },
        detailed_insights: {
          energy_flow:
            insightCards.find((c) => c.id === "energy_flow")?.description || "",
          emotional_connection:
            insightCards.find((c) => c.id === "emotional")?.description || "",
          action_timing:
            insightCards.find((c) => c.id === "action")?.description || "",
          communication_tips:
            insightCards.find((c) => c.id === "communication")?.description ||
            "",
        },
        ai_suggestion: {
          text: t("energy.aiSuggestion"),
          button_label: t("energy.chatWith", { bot: botName }),
        },
        summary: summaryItems,
      };
    } catch (error) {
      console.error("Error parsing AI response:", error);
      // Return default values
      return {
        compatibility_summary: {
          overall_score: 78,
          status_label: t("energy.statusLabel"),
        },
        life_graph: {
          categories: [
            t("energy.metric.emotionalFlow"),
            t("energy.metric.mentalRhythm"),
            t("energy.metric.actionDrive"),
            t("energy.metric.harmonyField"),
            t("energy.metric.commEnergy"),
          ],
          values: [65, 72, 58, 80, 70],
        },
        summary: [
          {
            type: "positive",
            title: t("energy.summary.good.title"),
            text: t("energy.summary.good.text"),
          },
          {
            type: "adjustment",
            title: t("energy.summary.tune.title"),
            text: t("energy.summary.tune.text"),
          },
        ],
      };
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorText("");

    // Country module mode: build native-language message and send via normal chat flow
    if (isCountryMode) {
      try {
        const message = buildCountryCompatibilityMessage(
          countryParam!,
          person1Name,
          person1Date,
          person1Time,
          person1Place,
          person2Name,
          person2Date,
          person2Time,
          person2Place,
          person2Relation,
        );
        const newChat = await startNewChat(
          categoryIdParam!,
          subCategoryIdParam!,
        );
        setCurrentChat(newChat);
        navigate(`/sanctuary?chat=${newChat.id}`);
        // Send the pre-built message after a short tick so ChatInterface has mounted
        setTimeout(() => {
          sendMessageStream(
            message,
            newChat.id,
            categoryIdParam,
            subCategoryIdParam,
          );
        }, 300);
      } catch (err) {
        console.error("Country mode energy match error:", err);
        setErrorText(t("energy.err.start"));
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const storedUserId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("userId")
          : null;
      const storedCategoryId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("categoryId")
          : null;
      const urlCategoryId = getCategoryId();

      const response = await api.post("/backend/energyMatch/create", {
        userId: storedUserId || null,
        CategoryId: urlCategoryId || storedCategoryId || null,
        name: person1Name,
        dob: person1Date,
        dob_time: person1Time,
        dob_place: person1Place,
        name_p: person2Name,
        dob_p: person2Date,
        dob_time_p: person2Time,
        dob_place_p: person2Place,
        relation_p: person2Relation,
      });

      // Extract the API response structure
      const apiResponse = response.data as ApiResponse;

      // Get the aiResponse string and parse it
      const aiResponseString = apiResponse.aiResponse;
      const parsed = parseAiResponse(aiResponseString);

      // console.log("History ID:", apiResponse.historyId);
      setResultData(parsed);
      setHistoryId(apiResponse.historyId || "");

      // Update chat messages if available from parsed data
      try {
        const cleaned = aiResponseString
          .replace(/```json/gi, "")
          .replace(/```/g, "")
          .trim();
        const jsonSafe = cleaned.replace(/,\s*([}\]])/g, "$1");
        const fullParsed = JSON.parse(jsonSafe);
        const chatPage = fullParsed.pages?.find(
          (p: any) => p.pageId === "P4_ChatWithHealjai",
        );
        if (chatPage) {
          if (chatPage.chatHistory && chatPage.chatHistory.length > 0) {
            setChatMessages(chatPage.chatHistory);
          }
          if (chatPage.quickReplies) {
            setQuickReplies(chatPage.quickReplies);
          }
        }
      } catch (e) {
        console.error("Error setting chat data:", e);
      }

      setCurrentPage("prediction");
    } catch (error) {
      console.error("API Error:", error);
      setErrorText(t("energy.err.calc"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewInsights = () => {
    setCurrentPage("insights");
  };

  const handleOpenChat = () => {
    setCurrentPage("chat");
  };

  const handleInsightClick = (insightId: string) => {
    setSelectedInsight(insightId);
  };

  const handleSendMessage = async (messageOverride?: string) => {
    const userMessage = messageOverride ?? chatInput;
    if (!userMessage.trim()) return;

    setChatMessages((prev) => [...prev, { sender: "User", text: userMessage }]);
    setChatInput("");
    setIsTyping(true);

    try {
      const storedUserId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("userId")
          : null;
      const storedCategoryId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("categoryId")
          : null;
      const urlCategoryId = getCategoryId();

      const response = await api.post("/backend/energyMatch/create", {
        userId: storedUserId || null,
        CategoryId: urlCategoryId || storedCategoryId || null,
        name: person1Name,
        dob: person1Date,
        dob_time: person1Time,
        dob_place: person1Place,
        name_p: person2Name,
        dob_p: person2Date,
        dob_time_p: person2Time,
        dob_place_p: person2Place,
        relation_p: person2Relation,
        question: userMessage,
        chatId: historyId,
      });

      const apiResponse = response.data as ApiResponse;
      const healjaiResponse = apiResponse.aiResponse
        .replace(/```json/gi, "")
        .replace(/```/g, "")
        .trim();

      setChatMessages((prev) => [
        ...prev,
        { sender: BOT_SENDER, text: healjaiResponse },
      ]);
    } catch (error) {
      console.error("Chat API Error:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          sender: BOT_SENDER,
          text: t("energy.err.chat"),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  // Radar chart component
  const RadarChart: React.FC<{ categories: string[]; values: number[] }> = ({
    categories,
    values,
  }) => {
    const size = 400;
    const center = size / 2;
    const radius = size * 0.3;
    const labelRadius = radius * 1.35;
    const maxLabelLength = 12;
    const angles = categories.map(
      (_, i) => (i * 2 * Math.PI) / categories.length - Math.PI / 2,
    );

    const points = angles.map((angle, i) => ({
      x: center + (values[i] / 100) * radius * Math.cos(angle),
      y: center + (values[i] / 100) * radius * Math.sin(angle),
    }));

    const polygonPoints = points.map((p) => `${p.x},${p.y}`).join(" ");

    const truncateLabel = (label: string, maxLength: number): string => {
      return label.length > maxLength
        ? label.substring(0, maxLength) + "..."
        : label;
    };

    const labelPositions = angles.map((angle, i) => ({
      x: center + labelRadius * Math.cos(angle),
      y: center + labelRadius * Math.sin(angle),
      label: truncateLabel(categories[i], maxLabelLength),
    }));

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {[0.2, 0.4, 0.6, 0.8, 1].map((level, idx) => (
          <polygon
            key={idx}
            points={angles
              .map((angle) => {
                const r = radius * level;
                const x = center + r * Math.cos(angle);
                const y = center + r * Math.sin(angle);
                return `${x},${y}`;
              })
              .join(" ")}
            fill="none"
            stroke="#D4C5F9"
            strokeWidth="1"
          />
        ))}
        {angles.map((angle, i) => (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={center + radius * Math.cos(angle)}
            y2={center + radius * Math.sin(angle)}
            stroke="#D4C5F9"
            strokeWidth="1"
          />
        ))}
        <polygon
          points={polygonPoints}
          fill="url(#gradient)"
          fillOpacity="0.35"
          stroke="#d9ccff"
          strokeWidth="2.5"
        />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill="#d9ccff" />
        ))}
        {labelPositions.map((pos, i) => (
          <text
            key={`label-${i}`}
            x={pos.x}
            y={pos.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="12"
            fontWeight="600"
            fill="#c8bfd1"
            style={{ pointerEvents: "none" }}
          >
            {pos.label}
          </text>
        ))}
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#d9ccff" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#C084FC" stopOpacity="0.6" />
          </linearGradient>
        </defs>
      </svg>
    );
  };

  const overallScore = resultData?.compatibility_summary?.overall_score ?? 78;
  const ringColor = "#C084FC";
  const ringDegrees = Math.min(Math.max(overallScore, 0), 100) * 3.6;

  // console.log("Parsed Result Data:", resultData);
  // console.log("Parsed Result Data 2:", resultData?.life_graph);

  const lifeGraphData = resultData?.life_graph || {
    categories: [
      t("energy.metric.emotionalFlow"),
      t("energy.metric.mentalRhythm"),
      t("energy.metric.actionDrive"),
      t("energy.metric.harmonyField"),
      t("energy.metric.commEnergy"),
    ],
    values: [65, 72, 58, 80, 70],
  };

  const getInsightContent = (id: string): string => {
    if (resultData?.detailed_insights) {
      switch (id) {
        case "energy_flow":
          return resultData.detailed_insights.energy_flow || "";
        case "emotional":
          return resultData.detailed_insights.emotional_connection || "";
        case "action":
          return resultData.detailed_insights.action_timing || "";
        case "communication":
          return resultData.detailed_insights.communication_tips || "";
        default:
          return "";
      }
    }
    return "";
  };

  const getInsightTitle = (id: string): string => {
    const titles: Record<string, string> = {
      energy_flow: t("energy.insight.energy_flow.title"),
      emotional: t("energy.insight.emotional.title"),
      action: t("energy.insight.action.title"),
      communication: t("energy.insight.communication.title"),
    };
    return titles[id] || id;
  };

  const getInsightIcon = (id: string): string => {
    const icons: Record<string, string> = {
      energy_flow: "🌊",
      emotional: "💖",
      action: "⏰",
      communication: "💬",
    };
    return icons[id] || "✨";
  };

  const insightCards = [
    { id: "energy_flow", title: t("energy.insight.energy_flow.title"), icon: "🌊" },
    { id: "emotional", title: t("energy.insight.emotional.title"), icon: "💖" },
    { id: "action", title: t("energy.insight.action.title"), icon: "⏰" },
    { id: "communication", title: t("energy.insight.communication.title"), icon: "💬" },
  ];

  // Get summary items from resultData
  const summaryItems = resultData?.summary || [
    {
      type: "positive" as const,
      title: t("energy.summary.good.title"),
      text: t("energy.summary.good.text"),
    },
    {
      type: "adjustment" as const,
      title: t("energy.summary.tune.title"),
      text: t("energy.summary.tune.text"),
    },
  ];

  const positiveSummary = summaryItems.find((item) => item.type === "positive");
  const adjustmentSummary = summaryItems.find(
    (item) => item.type === "adjustment",
  );

  // ========== RENDER PAGES ==========

  // Page 1: Form
  if (currentPage === "form") {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={pageTransition}
        className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark]"
      >
        <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 xl:px-20 py-10 sm:py-12 lg:py-16">
          <div className="w-full max-w-5xl mx-auto">
            <div className="mb-4 flex items-center justify-end">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1a131f] px-3 py-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#c8bfd1] shadow-sm transition hover:bg-[#221a29]"
              >
                <span className="text-lg sm:text-xl leading-none">←</span>
                <span>{t("energy.back")}</span>
              </button>
            </div>
            <div className="text-center mb-8 sm:mb-10 lg:mb-12">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-light text-[#c8bfd1] tracking-wide">
                {t("energy.titlePlain")}
              </h1>
              <p className="text-[#f7f3fa] text-xs sm:text-sm mt-2 max-w-lg mx-auto">
                {t("energy.enterBirth")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
                {/* LEFT: Person 1 */}
                <div className="space-y-5">
                  <div className="border-b border-white/10 pb-2 mb-2">
                    <span className="text-[#C084FC] text-xs font-semibold tracking-wider uppercase flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C084FC]"></span>
                      {t("energy.person", { n: 1 })}
                    </span>
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                      {t("energy.name")}
                    </label>
                    <input
                      type="text"
                      placeholder={t("energy.namePlaceholder")}
                      value={person1Name}
                      onChange={(e) => setPerson1Name(e.target.value)}
                      className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm transition-all focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                        {t("energy.dob")}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="DD / MM / YYYY"
                        value={person1Date}
                        onChange={(e) => handleDateChange(e, setPerson1Date)}
                        onBlur={(e) =>
                          validateDate(e.target.value, setPerson1Date)
                        }
                        className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                        {t("energy.timeIfAvailable")}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="HH : MM"
                        value={person1Time}
                        onChange={(e) => handleTimeChange(e, setPerson1Time)}
                        onBlur={(e) =>
                          validateTime(e.target.value, setPerson1Time)
                        }
                        className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                      {t("energy.placeIfAvailable")}
                    </label>
                    <input
                      type="text"
                      placeholder={t("energy.provincePlaceholder")}
                      value={person1Place}
                      onChange={(e) => setPerson1Place(e.target.value)}
                      className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                    />
                  </div>
                </div>

                {/* RIGHT: Person 2 */}
                <div className="space-y-5">
                  <div className="border-b border-white/10 pb-2 mb-2">
                    <span className="text-[#7DD3FC] text-xs font-semibold tracking-wider uppercase flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#7DD3FC]"></span>
                      {t("energy.person", { n: 2 })}
                    </span>
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                      {t("energy.name")}
                    </label>
                    <input
                      type="text"
                      placeholder={t("energy.partnerNamePlaceholder")}
                      value={person2Name}
                      onChange={(e) => setPerson2Name(e.target.value)}
                      className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                      {t("energy.relationship")}
                    </label>
                    <select
                      value={person2Relation}
                      onChange={(e) => setPerson2Relation(e.target.value)}
                      className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                    >
                      <option value="Friend">{t("energy.rel.Friend")}</option>
                      <option value="Colleague">{t("energy.rel.Colleague")}</option>
                      <option value="Family">{t("energy.rel.Family")}</option>
                      <option value="Partner">{t("energy.rel.Partner")}</option>
                      <option value="Other">{t("energy.rel.Other")}</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                        {t("energy.dob")}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="DD / MM / YYYY"
                        value={person2Date}
                        onChange={(e) => handleDateChange(e, setPerson2Date)}
                        onBlur={(e) =>
                          validateDate(e.target.value, setPerson2Date)
                        }
                        className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                        {t("energy.timeIfAvailable")}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="HH : MM"
                        value={person2Time}
                        onChange={(e) => handleTimeChange(e, setPerson2Time)}
                        onBlur={(e) =>
                          validateTime(e.target.value, setPerson2Time)
                        }
                        className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
                      {t("energy.placeIfAvailable")}
                    </label>
                    <input
                      type="text"
                      placeholder={t("energy.provincePlaceholder")}
                      value={person2Place}
                      onChange={(e) => setPerson2Place(e.target.value)}
                      className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
                    />
                  </div>
                </div>
              </div>

              {errorText && (
                <div className="text-center text-xs sm:text-sm text-red-500">
                  {errorText}
                </div>
              )}

              <div className="flex justify-center pt-6">
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="w-full sm:w-auto min-w-[240px] sm:min-w-[280px] md:min-w-[320px] rounded-[14px] bg-[#a78bff] py-3 sm:py-4 px-8 sm:px-10 text-sm sm:text-[15px] md:text-base font-semibold text-[#0f0a14] shadow-[0_0_22px_rgba(167,139,255,0.35)] hover:brightness-110 transition-all duration-300 active:scale-[0.99] focus:ring-2 focus:ring-[#C084FC] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-3">
                      <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      {t("energy.calculating")}
                    </span>
                  ) : (
                    t("energy.check")
                  )}
                </motion.button>
              </div>

              <div className="text-center mt-6 text-[#f7f3fa] text-[10px] sm:text-[11px] tracking-wide">
                <span className="inline-block border-t border-white/10 w-12 mx-2 align-middle"></span>
                {t("energy.checkSub")}
                <span className="inline-block border-t border-white/10 w-12 mx-2 align-middle"></span>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    );
  }

  // Page 2: Prediction
  // Page 2: Prediction - Improved layout with horizontal arrangement
  if (currentPage === "prediction") {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={pageTransition}
        className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark]"
      >
        <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 xl:px-20 py-10 sm:py-12 lg:py-16">
          <div className="w-full max-w-6xl mx-auto">
            <div className="mb-4 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setCurrentPage("form")}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1a131f] px-3 py-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#c8bfd1] shadow-sm transition hover:bg-[#221a29]"
              >
                <span className="text-lg sm:text-xl leading-none">←</span>
                <span>{t("energy.back")}</span>
              </button>
            </div>

            <div className="text-center mb-8 sm:mb-10 lg:mb-12 animate-fade-in">
              <h1 className="text-lg sm:text-xl md:text-2xl font-light text-[#c8bfd1] tracking-wide">
                {t("energy.yourResults")}
              </h1>
              <p className="text-[#f7f3fa] text-[10px] sm:text-xs mt-1 tracking-wide uppercase">
                {person1Name || "Person 1"} & {person2Name || "Person 2"}
              </p>
              {resultData?.compatibility_summary?.status_label && (
                <p className="text-[#C084FC] text-[10px] sm:text-xs mt-2 font-semibold tracking-wide">
                  {resultData.compatibility_summary.status_label}
                </p>
              )}
            </div>

            <div className="space-y-8 sm:space-y-10">
              {/* Score and Life Graph side by side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Score Circle - Left side */}
                <div
                  className="flex justify-center animate-fade-in-up"
                  style={{ animationDelay: "0.1s" }}
                >
                  <div
                    className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 rounded-full p-[6px] transition-all duration-700 ease-out"
                    style={{
                      background: `conic-gradient(${ringColor} 0deg, ${ringColor} ${ringDegrees}deg, rgba(192,132,252,0.15) ${ringDegrees}deg 360deg)`,
                    }}
                  >
                    <div className="w-full h-full rounded-full bg-[#221a29] border border-white/10 flex items-center justify-center">
                      <div className="w-40 h-40 sm:w-44 sm:h-44 md:w-52 md:h-52 rounded-full border border-white/10 flex flex-col items-center justify-center bg-[#1a131f] shadow-none">
                        <span className="text-[10px] sm:text-xs tracking-[0.2em] text-[#c8bfd1] uppercase">
                          {t("energy.alignmentScore")}
                        </span>
                        <div className="mt-2 text-[#c8bfd1] font-light">
                          <span className="text-5xl sm:text-6xl md:text-7xl font-medium">
                            {overallScore}
                          </span>
                          <span className="text-xl sm:text-2xl md:text-3xl">
                            /100
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Life Graph - Right side */}
                <div
                  className="flex flex-col items-center animate-fade-in-up"
                  style={{ animationDelay: "0.15s" }}
                >
                  <h3 className="text-sm sm:text-base font-semibold text-[#c8bfd1] mb-4">
                    {t("energy.lifeGraph")}
                  </h3>
                  <RadarChart
                    categories={lifeGraphData.categories}
                    values={lifeGraphData.values}
                  />
                  <div className="grid grid-cols-5 gap-2 sm:gap-4 mt-4 text-center">
                    {lifeGraphData.categories.map((cat, idx) => (
                      <div
                        key={idx}
                        className="text-[8px] sm:text-[10px] text-[#f7f3fa] max-w-[60px] break-words"
                      >
                        {cat}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in-up"
                style={{ animationDelay: "0.3s" }}
              >
                {positiveSummary && (
                  <div className="bg-[#1a131f] rounded-2xl border border-white/10 p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="text-lg sm:text-xl text-[#C084FC]">✨</div>
                    <div className="text-xs sm:text-sm text-[#c8bfd1] font-semibold uppercase mt-2">
                      {positiveSummary.title}
                    </div>
                    <p className="mt-2 text-[11px] sm:text-sm text-[#f7f3fa] leading-5">
                      {positiveSummary.text}
                    </p>
                  </div>
                )}
                {adjustmentSummary && (
                  <div className="bg-[#1a131f] rounded-2xl border border-white/10 p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="text-lg sm:text-xl text-[#7DD3FC]">🔄</div>
                    <div className="text-xs sm:text-sm text-[#c8bfd1] font-semibold uppercase mt-2">
                      {adjustmentSummary.title}
                    </div>
                    <p className="mt-2 text-[11px] sm:text-sm text-[#f7f3fa] leading-5">
                      {adjustmentSummary.text}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div
                className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in-up"
                style={{ animationDelay: "0.4s" }}
              >
                <button
                  type="button"
                  onClick={handleViewInsights}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 rounded-full border-2 border-[#C084FC] bg-transparent text-[#C084FC] font-semibold text-sm sm:text-base transition-all hover:bg-[#C084FC] hover:text-white hover:shadow-md"
                >
                  {t("energy.viewInsights")}
                </button>
                <button
                  type="button"
                  onClick={handleOpenChat}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 rounded-[14px] bg-[#a78bff] text-[#0f0a14] font-semibold text-sm sm:text-base shadow-[0_0_22px_rgba(167,139,255,0.35)] hover:shadow-[0_0_22px_rgba(167,139,255,0.35)] transition-all "
                >
                  {t("energy.chatWith", { bot: botName })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Page 3: Insights
  if (currentPage === "insights") {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={pageTransition}
        className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark]"
      >
        <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 xl:px-20 py-10 sm:py-12 lg:py-16">
          <div className="w-full max-w-5xl mx-auto">
            <div className="mb-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setCurrentPage("prediction")}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1a131f] px-3 py-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#c8bfd1] shadow-sm transition hover:bg-[#221a29]"
              >
                <span className="text-lg sm:text-xl leading-none">←</span>
                <span>{t("energy.back")}</span>
              </button>
              <button
                type="button"
                onClick={handleOpenChat}
                className="inline-flex items-center gap-2 rounded-[14px] bg-[#a78bff] px-4 py-2 text-[11px] sm:text-xs font-semibold text-[#0f0a14] shadow-[0_0_22px_rgba(167,139,255,0.35)] transition hover:shadow-[0_0_22px_rgba(167,139,255,0.35)]"
              >
                <span>💬</span>
                <span>{t("energy.chatWith", { bot: botName })}</span>
              </button>
            </div>

            <div className="text-center mb-8 sm:mb-10 lg:mb-12">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-light text-[#c8bfd1] tracking-wide">
                {t("energy.insightsTitle")}
              </h1>
              <p className="text-[#f7f3fa] text-xs sm:text-sm mt-2">
                {t("energy.tapCards")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {insightCards.map((card, idx) => (
                <div
                  key={card.id}
                  onClick={() => handleInsightClick(card.id)}
                  className="group bg-[#1a131f] rounded-2xl border border-white/10 p-5 sm:p-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer transform hover:-translate-y-1 animate-fade-in-up"
                  style={{ animationDelay: `${0.1 + idx * 0.05}s` }}
                >
                  <div className="flex items-start justify-between">
                    <div className="text-3xl sm:text-4xl">{card.icon}</div>
                    <div className="text-[#C084FC] opacity-0 group-hover:opacity-100 transition-opacity">
                      →
                    </div>
                  </div>
                  <div className="text-sm sm:text-base font-semibold text-[#c8bfd1] mt-3">
                    {card.title}
                  </div>
                  <p className="mt-2 text-[11px] sm:text-sm text-[#f7f3fa] leading-5 line-clamp-2">
                    {getInsightContent(card.id).substring(0, 100)}...
                  </p>
                </div>
              ))}
            </div>

            {selectedInsight && (
              <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={() => setSelectedInsight(null)}
              >
                <div
                  className="bg-[#1a131f] rounded-2xl max-w-md w-full p-6 shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {getInsightIcon(selectedInsight)}
                      </span>
                      <h3 className="text-lg font-semibold text-[#c8bfd1]">
                        {getInsightTitle(selectedInsight)}
                      </h3>
                    </div>
                    <button
                      onClick={() => setSelectedInsight(null)}
                      className="text-[#c8bfd1] hover:text-[#f7f3fa] text-xl"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-[#f7f3fa] leading-relaxed">
                    {getInsightContent(selectedInsight)}
                  </p>
                  <button
                    onClick={() => {
                      setSelectedInsight(null);
                      handleOpenChat();
                    }}
                    className="mt-6 w-full rounded-full bg-[#a78bff] py-2.5 text-sm font-semibold text-[#0f0a14]"
                  >
                    {t("energy.chatWithAdvice", { bot: botName })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Page 4: Chat - Improved version
  // Page 4: Chat - Improved version with better button visibility
  if (currentPage === "chat") {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={pageTransition}
        className="min-h-screen w-full bg-gradient-to-br from-[#221a29] to-[#0f0a14]"
      >
        <div className="min-h-screen w-full flex flex-col">
          {/* Header - Back button on right side */}
          <div className="bg-[#1a131f]/80 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4 sticky top-0 z-10 shadow-sm">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-[#a78bff] flex items-center justify-center text-[#0f0a14] text-base shadow-[0_0_22px_rgba(167,139,255,0.35)]">
                    H
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0f0a14]"></div>
                </div>
                <div>
                  <div className="text-base font-semibold text-[#c8bfd1]">
                    {botName}
                  </div>
                  <div className="text-[10px] text-green-600 font-medium">
                    {t("energy.online")}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCurrentPage("prediction")}
                className="inline-flex items-center gap-2 rounded-[14px] bg-[#a78bff] px-4 py-2 text-[11px] sm:text-xs font-semibold text-[#0f0a14] shadow-[0_0_22px_rgba(167,139,255,0.35)] hover:shadow-[0_0_22px_rgba(167,139,255,0.35)] transition-all "
              >
                <span>{t("energy.backToResults")}</span>
                <span className="text-base leading-none">→</span>
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
            <div className="max-w-4xl mx-auto space-y-4">
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.sender === "User" ? "justify-end" : "justify-start"} animate-fade-in-up`}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  {msg.sender === BOT_SENDER && (
                    <div className="w-8 h-8 rounded-full bg-[#a78bff] flex items-center justify-center text-[#0f0a14] text-xs mr-2 flex-shrink-0 shadow-[0_0_22px_rgba(167,139,255,0.35)]">
                      H
                    </div>
                  )}
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                      msg.sender === "User"
                        ? "bg-[#a78bff] text-[#0f0a14] shadow-[0_0_22px_rgba(167,139,255,0.35)]"
                        : "bg-[#1a131f] border border-white/10 text-[#f7f3fa] shadow-[0_0_22px_rgba(167,139,255,0.35)]"
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                    <div
                      className={`text-[9px] mt-1 ${msg.sender === "User" ? "text-purple-100" : "text-[#c8bfd1]"}`}
                    >
                      {new Date().toLocaleTimeString(toLocale(getAppLanguage()), {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  {msg.sender === "User" && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#2a2133] to-[#221a29] flex items-center justify-center text-[#c8bfd1] text-xs ml-2 flex-shrink-0 shadow-md">
                      {person1Name?.charAt(0).toUpperCase() || "U"}
                    </div>
                  )}
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start animate-fade-in-up">
                  <div className="w-8 h-8 rounded-full bg-[#a78bff] flex items-center justify-center text-[#0f0a14] text-xs mr-2 flex-shrink-0 shadow-[0_0_22px_rgba(167,139,255,0.35)]">
                    H
                  </div>
                  <div className="bg-[#1a131f] border border-white/10 rounded-2xl px-4 py-3 shadow-sm">
                    <div className="flex gap-1">
                      <span
                        className="w-2 h-2 bg-[#C084FC] rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      ></span>
                      <span
                        className="w-2 h-2 bg-[#C084FC] rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      ></span>
                      <span
                        className="w-2 h-2 bg-[#C084FC] rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      ></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Quick Replies */}
          {quickReplies.length > 0 && (
            <div className="border-t border-white/10 px-4 sm:px-6 py-3 bg-[#1a131f]/80 backdrop-blur-sm">
              <div className="max-w-4xl mx-auto">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {quickReplies.map((reply, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(reply)}
                      className="px-4 py-2 rounded-full border-2 border-[#C084FC] bg-[#1a131f] text-[#C084FC] text-xs font-medium whitespace-nowrap hover:bg-[#C084FC] hover:text-white transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Message Input - Fixed button visibility */}
          <div className="border-t border-white/10 px-4 sm:px-6 py-4 bg-[#1a131f]">
            <div className="max-w-4xl mx-auto flex gap-3">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                placeholder={t("energy.typePlaceholder")}
                className="flex-1 rounded-full border border-white/10 bg-[#1a131f] px-5 py-3 text-sm text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 focus:border-[#C084FC] focus:outline-none focus:ring-2 focus:ring-[#C084FC]/20 transition-all"
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!chatInput.trim()}
                className="rounded-full bg-[#6D4C92] px-6 py-3 text-white font-semibold text-sm shadow-md hover:bg-[#5B3D7A] hover:shadow-lg transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {t("energy.send")}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};

export default EnergyMatchDesign;
